import { Package, FlaskConical, Check } from "lucide-react";

/**
 * Two research-buying paths presented side by side, no "recommended" bias.
 * Bone section bg, white cards, oxblood accents on numeric details.
 */

const TIERS = [
  {
    key: "supply",
    overline: "For value-focused researchers",
    title: "Research Supply.",
    lede: "Tier-priced kits built for stronger per-vial economics.",
    icon: Package,
    features: [
      "3-vial kits",
      "Best per-vial pricing",
      "3rd-party tested · COA available",
    ],
  },
  {
    key: "select",
    overline: "For flexible research needs",
    title: "Research Select.",
    lede: "Single vials and smaller multi-vial kits for targeted purchases.",
    icon: FlaskConical,
    features: [
      "Single, 3- or 5-vial options",
      "Mix & match available",
      "3rd-party tested · COA available",
    ],
  },
];

export default function LandingTiersSection() {
  return (
    <section
      className="relative bg-bone border-t border-borderSubtle py-20 sm:py-28 lg:py-32"
      data-testid="landing-tiers"
      aria-label="Research buying options"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="text-[13px] uppercase tracking-[0.32em] text-oxblood font-mono">
            Two ways to buy
          </p>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal tracking-tight leading-[1.02]">
            Pick the path that <span className="italic text-charcoal/85">fits your bench.</span>
          </h2>
          <p className="mt-5 max-w-[54ch] text-base text-stone leading-relaxed">
            Whether you're stocking a lab or running a targeted study, there's a
            format built for how you work. Same catalog, same sourcing — different scale.
          </p>
        </div>

        {/* Two-tier grid */}
        <div className="mt-14 sm:mt-16 grid md:grid-cols-2 gap-6 lg:gap-8">
          {TIERS.map((t) => (
            <TierCard key={t.key} tier={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TierCard({ tier }) {
  const Icon = tier.icon;
  return (
    <article
      className="relative bg-white border border-borderSubtle rounded-[6px] p-8 sm:p-10 lg:p-12 flex flex-col transition-shadow duration-500 hover:shadow-[0_30px_60px_-40px_rgba(26,26,26,0.20)]"
      data-testid="landing-tier-card"
    >
      <CornerMarks />

      {/* Icon tile */}
      <div
        className="relative w-14 h-14 rounded-[8px] grid place-items-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(74,14,23,0.08), rgba(140,123,102,0.06))",
        }}
        aria-hidden
      >
        <Icon size={22} strokeWidth={1.5} className="text-oxblood" />
      </div>

      {/* Overline */}
      <p className="mt-8 text-[12px] uppercase tracking-[0.28em] text-stone font-mono">
        {tier.overline}
      </p>

      {/* Title */}
      <h3 className="mt-3 font-serif text-3xl sm:text-4xl text-charcoal tracking-tight leading-[1.02]">
        {tier.title}
      </h3>

      {/* Lede */}
      <p className="mt-4 text-sm sm:text-base text-stone leading-relaxed max-w-[42ch]">
        {tier.lede}
      </p>

      {/* Feature list */}
      <ul className="mt-8 divide-y divide-borderSubtle border-t border-b border-borderSubtle">
        {tier.features.map((f) => (
          <li key={f} className="flex items-center gap-3 py-3.5">
            <span
              className="grid place-items-center w-5 h-5 rounded-full border border-oxblood/50 text-oxblood shrink-0"
              aria-hidden
            >
              <Check size={11} strokeWidth={2.5} />
            </span>
            <span className="font-serif text-base sm:text-lg text-charcoal">{f}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function CornerMarks() {
  const stroke = "rgba(26,26,26,0.22)";
  const size = 10;
  const Mark = ({ style }) => (
    <span className="absolute" style={style} aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={`M0 0 H${size} M0 0 V${size}`} stroke={stroke} strokeWidth="1" />
      </svg>
    </span>
  );
  return (
    <>
      <Mark style={{ top: 10, left: 10 }} />
      <Mark style={{ top: 10, right: 10, transform: "scaleX(-1)" }} />
      <Mark style={{ bottom: 10, left: 10, transform: "scaleY(-1)" }} />
      <Mark style={{ bottom: 10, right: 10, transform: "scale(-1,-1)" }} />
    </>
  );
}
